// Utility for securely storing credentials in IndexedDB

import { ProviderToken } from "sched";

// Interface for stored credentials
export interface StoredCredential {
  id: string;
  value: string;
  workspaceId: string;
  createdAt: number;
  updatedAt: number;
}

export type CredentialValue = {
  type: 'pat';
  provider: "github" | "gitlab";
  value: string;
} | {
  type: "bearer_token";
  provider: "github" | "gitlab";
  issuer: string;
  access_token: string;
  id_token?: string;
  refresh_token?: string;
}

export const credentialTypeLabels: Record<CredentialValue['type'], string> = {
  pat: 'Personal Access Token (PAT)',
  bearer_token: 'Bearer Token'
}

export const credentialProviderLabels: Record<CredentialValue['provider'], string> = {
  github: 'GitHub',
  gitlab: 'GitLab'
}

// Constants
// This should be different from the one used by Rust so that they can be versioned/migrated separately
// I ran into problems trying to share the db_name but use different store names
const DB_NAME = 'chronomap-js';
const STORE_NAME = 'credentials';

/**
 * Delete the existing database to force recreation
 * This is a temporary fix for database schema issues
 */
export const resetCredentialDb = (): Promise<void> => {
  return new Promise((resolve) => {
    const deleteRequest = indexedDB.deleteDatabase(DB_NAME);

    deleteRequest.onerror = () => {
      console.warn('Error deleting credential database');
      // Continue even if delete fails
      resolve();
    };

    deleteRequest.onsuccess = () => {
      console.log('Successfully deleted credential database');
      resolve();
    };
  });
};

/**
 * Initialize the IndexedDB database
 */
export const initCredentialDb = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    // First try to open without version to check what exists
    const checkRequest = indexedDB.open(DB_NAME);

    checkRequest.onerror = (event) => {
      console.error('Database check error:', event);
      reject(new Error('Failed to open credential database for checking'));
    };

    checkRequest.onsuccess = () => {
      const db = checkRequest.result;
      const currentVersion = db.version;

      // Check if our store exists
      const hasCredentialsStore = db.objectStoreNames.contains(STORE_NAME);
      db.close();

      if (hasCredentialsStore) {
        // Store exists, just open normally
        const request = indexedDB.open(DB_NAME);
        request.onerror = (event) => {
          console.error('Database open error:', event);
          reject(new Error('Failed to open credential database'));
        };
        request.onsuccess = () => {
          resolve(request.result);
        };
      } else {
        // Need to create our store, increment version
        const newVersion = currentVersion + 1;
        console.log(`Need to create ${STORE_NAME} store, upgrading from version ${currentVersion} to ${newVersion}`);

        const upgradeRequest = indexedDB.open(DB_NAME, newVersion);

        upgradeRequest.onerror = (event) => {
          console.error('Database upgrade error:', event);
          reject(new Error('Failed to upgrade credential database'));
        };

        upgradeRequest.onsuccess = () => {
          resolve(upgradeRequest.result);
        };

        upgradeRequest.onupgradeneeded = (event) => {
          console.log(`Creating ${STORE_NAME} store during upgrade from ${event.oldVersion} to ${event.newVersion}`);
          const db = upgradeRequest.result;

          if (!db.objectStoreNames.contains(STORE_NAME)) {
            const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            store.createIndex('workspaceId', 'workspaceId', { unique: false });
          }
        };
      }
    };
  });
};

/**
 * Get a database connection
 */
export const getCredentialDb = async (): Promise<IDBDatabase> => {
  try {
    return await initCredentialDb();
  } catch (error) {
    console.error('Error initializing credential database:', error);
    throw error;
  }
};

/**
 * Save a credential to IndexedDB
 */
export const saveCredential = async (
  workspaceId: string,
  credentialId: string,
  value: string
): Promise<StoredCredential> => {
  const db = await getCredentialDb();

  const now = Date.now();
  const credential: StoredCredential = {
    id: credentialId,
    value,
    workspaceId,
    createdAt: now,
    updatedAt: now,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const request = store.put(credential);

    request.onsuccess = () => {
      resolve(credential);
    };

    request.onerror = () => {
      reject(new Error('Failed to save credential'));
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

/**
 * Get all credentials for a workspace
 */
export const getWorkspaceCredentials = async (workspaceId: string): Promise<StoredCredential[]> => {
  const db = await getCredentialDb();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('workspaceId');

    const request = index.getAll(workspaceId);

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(new Error('Failed to get credentials'));
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

/**
 * Get a credential by ID
 */
export const getCredentialById = async (
  workspaceId: string,
  credentialId: string
): Promise<StoredCredential | null> => {
  const db = await getCredentialDb();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    const request = store.get(credentialId);

    request.onsuccess = () => {
      const credential = request.result;
      if (credential && credential.workspaceId === workspaceId) {
        resolve(credential);
      } else {
        resolve(null);
      }
    };

    request.onerror = () => {
      reject(new Error('Failed to get credential'));
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

/**
 * Delete a credential
 */
export const deleteCredential = async (credentialId: string): Promise<void> => {
  const db = await getCredentialDb();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const request = store.delete(credentialId);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error('Failed to delete credential'));
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

/**
 * Get the decrypted value of a credential
 */
export const getDecryptedCredentialValue = async (
  workspaceId: string,
  credentialId: string
): Promise<CredentialValue | null> => {
  try {
    const credential = await getCredentialById(workspaceId, credentialId);
    if (!credential) return null;
    // Parse the credential value to get the structured value
    const credentialValue = JSON.parse(credential.value) as CredentialValue;

    // Return the actual token value based on credential type
    return credentialValue;
  } catch (error) {
    console.error('Error getting decrypted credential:', error);
    return null;
  }
};

export const getProviderToken = async (workspaceId: string, credentialId: string): Promise<ProviderToken | null> => {
  const credentialValue = await getDecryptedCredentialValue(workspaceId, credentialId);
  if (!credentialValue) return null;
  if (credentialValue.type === 'pat') {
    return { type: 'PAT', value: credentialValue.value };
  } else if (credentialValue.type === 'bearer_token') {
    // TODO also check & refresh token if needed
    return { type: 'JWTAccessToken', value: credentialValue.access_token };
  } else {
    return null;
  }
};

